import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import CrateMark from './CrateMark'

const NAV_LINKS = [
  { name: 'Services', href: '#services' },
  { name: 'Work', href: '#work' },
  { name: 'Process', href: '#process' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'About', href: '#about' },
  { name: 'Contact', href: '#contact' },
]

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const goTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
    setIsMobileOpen(false)
  }

  const goHome = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setIsMobileOpen(false)
  }

  return (
    <>
      <motion.nav
        initial={{ y: -90 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'border-b border-[var(--line)] bg-cream/80 backdrop-blur-xl'
            : 'border-b border-transparent bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-container items-center justify-between px-5 lg:px-8">
          {/* Logo */}
          <button onClick={goHome} className="group flex items-center gap-2.5" aria-label="CrateWeb home">
            <CrateMark className="h-8 w-8 transition-transform duration-300 group-hover:-translate-y-0.5" />
            <span className="font-display text-[1.35rem] font-extrabold tracking-tight text-ink">
              Crate<span className="text-gold">Web</span>
            </span>
          </button>

          {/* Desktop */}
          <div className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.name}
                onClick={() => goTo(link.href)}
                className="text-[0.92rem] font-medium text-ink-soft transition-colors hover:text-ink"
              >
                {link.name}
              </button>
            ))}
            <button onClick={() => goTo('#contact')} className="btn btn-primary !px-5 !py-2.5 text-[0.9rem]">
              Get a quote
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileOpen((v) => !v)}
            className="p-2 text-ink lg:hidden"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-cream px-6 pt-24 lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.name}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  onClick={() => goTo(link.href)}
                  className="border-b border-[var(--line)] py-4 text-left font-display text-2xl font-bold text-ink"
                >
                  {link.name}
                </motion.button>
              ))}
              <button onClick={() => goTo('#contact')} className="btn btn-primary mt-6 w-full">
                Get a quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navigation
